import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Check, Clock3, Minus, Users, X } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useMatch, useParticipantPredictions, useSavePrediction, useTeamRecentForm } from "@/modules/leagues/hooks/useMatches";
import { isLikelyStillLive } from "@/modules/leagues/repositories/shared/matchPredictionsHelpers";
import { ScoreStepper } from "@/modules/leagues/components/ScoreStepper";
import { Badge } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { Skeleton } from "@/components/atoms/Skeleton";
import { ErrorState } from "@/components/organisms/ErrorState";
import { Avatar, AvatarFallback } from "@/components/atoms/Avatar";
import { useToast } from "@/hooks/use-toast";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils/cn";

function formatKickoff(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }),
    time: d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
  };
}

function FormBadge({ result }: { result: "V" | "E" | "D" }) {
  if (result === "V") {
    return (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-700 text-white">
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (result === "D") {
    return (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
        <X className="h-3.5 w-3.5" />
      </span>
    );
  }
  return (
    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-muted-foreground">
      <Minus className="h-3.5 w-3.5" />
    </span>
  );
}

function TeamFormRow({ teamId, teamName, crestUrl }: { teamId: string; teamName: string; crestUrl: string | null }) {
  const { data: form, isLoading } = useTeamRecentForm(teamId);
  return (
    <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2.5">
      <div className="flex items-center gap-2">
        {crestUrl && <img src={crestUrl} alt={teamName} className="h-6 w-6 object-contain" />}
        <span className="text-xs font-semibold uppercase tracking-wide">{teamName}</span>
      </div>
      {isLoading ? (
        <Skeleton className="h-6 w-28" />
      ) : form && form.length > 0 ? (
        <div className="flex items-center gap-1">
          {form.map((f) => (
            <FormBadge key={f.matchId} result={f.result} />
          ))}
        </div>
      ) : (
        <span className="text-[11px] text-muted-foreground">Sem dados recentes</span>
      )}
    </div>
  );
}

/**
 * Detalhes da partida — tela dedicada aberta ao tocar num jogo na aba
 * Palpites (pedido do usuário, com referência visual de outro app).
 * Reaproveita as MESMAS regras de segurança e a mesma função de cálculo
 * (ScoringEngine) já usadas no MatchCard — isto é só uma apresentação
 * diferente do mesmo dado, nunca uma lógica paralela.
 */
export function MatchDetailPage() {
  const { league } = useLeagueContext();
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState<"detalhes" | "palpites">("detalhes");

  const { data: match, isLoading, isError, refetch } = useMatch(league.id, matchId);
  const [homeInput, setHomeInput] = useState(0);
  const [awayInput, setAwayInput] = useState(0);
  const savePrediction = useSavePrediction(league.id, match?.round);

  const isFinished = match?.status === "finished";
  const isLive = match?.status === "live";
  // Mesmo critério compartilhado (isLikelyStillLive) usado em
  // MatchCard.tsx — nunca confiar só no status cru da API (pode demorar a
  // atualizar), mas também nunca sem teto de tempo (bug real corrigido:
  // partida de ontem presa em "scheduled" não pode "revelar" como se
  // fosse ao vivo hoje).
  const canRevealParticipants = isLive || isFinished || (Boolean(match) && isLikelyStillLive(match!));
  const canPredict = match?.status === "scheduled" && !match?.predictionsLocked;

  const { data: participants } = useParticipantPredictions(
    canRevealParticipants ? league.id : undefined,
    canRevealParticipants ? match?.id : undefined
  );

  useEffect(() => {
    if (match?.userPrediction) {
      setHomeInput(match.userPrediction.homeScore);
      setAwayInput(match.userPrediction.awayScore);
    }
  }, [match?.userPrediction]);

  function handleSave() {
    if (!match) return;
    savePrediction.mutate(
      { matchId: match.id, homeScore: homeInput, awayScore: awayInput },
      {
        onSuccess: () => toast({ title: "Palpite salvo!" }),
        onError: (err) =>
          toast({
            title: "Não foi possível salvar seu palpite",
            description: err instanceof Error ? err.message : "Tente novamente.",
            variant: "destructive",
          }),
      }
    );
  }

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(ROUTES.leaguePredictions(league.id))}
          aria-label="Voltar"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="font-display text-base font-bold">Detalhes da partida</h1>
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {isError && !match && (
        <ErrorState title="Não foi possível carregar a partida" description="Verifique sua conexão e tente novamente." onRetry={() => refetch()} />
      )}

      {isError && match && (
        <p className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-center text-xs text-amber-800 dark:text-warning">
          Não deu pra atualizar o placar agora — mostrando os últimos dados carregados.
        </p>
      )}

      {match && (
        <>
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
            <div className="flex items-center gap-3 px-4 py-4">
              <div className="flex flex-1 flex-col items-center gap-1.5 text-center">
                {match.homeTeam.crestUrl && (
                  <img src={match.homeTeam.crestUrl} alt={match.homeTeam.name} className="h-11 w-11 object-contain" />
                )}
                <span className="line-clamp-1 text-xs font-medium">{match.homeTeam.name}</span>
              </div>

              <div className="flex flex-col items-center gap-1">
                {isFinished || isLive ? (
                  <span className="font-display text-2xl font-bold tabular-nums">
                    {match.homeScore} × {match.awayScore}
                  </span>
                ) : (
                  <Badge variant="warning">Palpitar</Badge>
                )}
                {isLive && (
                  <Badge variant="default" className="border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                    <span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-500" /> Ao vivo
                  </Badge>
                )}
              </div>

              <div className="flex flex-1 flex-col items-center gap-1.5 text-center">
                {match.awayTeam.crestUrl && (
                  <img src={match.awayTeam.crestUrl} alt={match.awayTeam.name} className="h-11 w-11 object-contain" />
                )}
                <span className="line-clamp-1 text-xs font-medium">{match.awayTeam.name}</span>
              </div>
            </div>
          </div>

          <div>
            <h2 className="mb-2 font-display text-sm font-bold">Seu palpite</h2>
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-around">
                <ScoreStepper
                  label={match.homeTeam.name.toUpperCase()}
                  value={homeInput}
                  onChange={setHomeInput}
                  disabled={!canPredict}
                />
                <ScoreStepper
                  label={match.awayTeam.name.toUpperCase()}
                  value={awayInput}
                  onChange={setAwayInput}
                  disabled={!canPredict}
                />
              </div>
            </div>
            <Button
              className="mt-3 w-full"
              disabled={!canPredict || savePrediction.isPending}
              onClick={handleSave}
            >
              <Check className="h-4 w-4" />
              {canPredict ? "Confirmar palpite" : "Palpites encerrados"}
            </Button>
          </div>

          <div className="flex items-center gap-1 rounded-xl border border-border bg-card p-1">
            {(["detalhes", "palpites"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cn(
                  "flex-1 rounded-lg py-2 text-xs font-semibold font-display capitalize transition-colors",
                  tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                )}
              >
                {t}
              </button>
            ))}
          </div>

          {tab === "detalhes" && (
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 font-display text-sm font-bold">Data da partida</h3>
                <div className="flex items-center gap-4 rounded-xl border border-border bg-card px-3 py-2.5 text-sm">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-muted-foreground" /> {formatKickoff(match.kickoff).date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock3 className="h-4 w-4 text-muted-foreground" /> {formatKickoff(match.kickoff).time}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5">
                <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                {canRevealParticipants ? (
                  <span className="text-sm">
                    <strong>{participants?.length ?? 0}</strong> de {league.participantsCount} palpitaram
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    A quantidade de palpites fica visível depois que a partida começa.
                  </span>
                )}
              </div>

              <div>
                <h3 className="mb-2 font-display text-sm font-bold">Últimos resultados</h3>
                <div className="space-y-2">
                  <TeamFormRow teamId={match.homeTeam.id} teamName={match.homeTeam.name} crestUrl={match.homeTeam.crestUrl} />
                  <TeamFormRow teamId={match.awayTeam.id} teamName={match.awayTeam.name} crestUrl={match.awayTeam.crestUrl} />
                </div>
              </div>
            </div>
          )}

          {tab === "palpites" && (
            <div className="space-y-2">
              {!canRevealParticipants && (
                <p className="rounded-xl border border-border bg-card px-3 py-4 text-center text-sm text-muted-foreground">
                  Os palpites de outros participantes ficam visíveis depois que a partida começa.
                </p>
              )}
              {canRevealParticipants && (!participants || participants.length === 0) && (
                <p className="rounded-xl border border-border bg-card px-3 py-4 text-center text-sm text-muted-foreground">
                  Ninguém palpitou nesta partida.
                </p>
              )}
              {canRevealParticipants &&
                participants?.map((p) => (
                  <div key={p.uid} className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
                    <Avatar size="sm">
                      <AvatarFallback>
                        {p.nome
                          .split(" ")
                          .slice(0, 2)
                          .map((n) => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex-1 truncate text-sm">{p.nome}</span>
                    <span className="font-display text-sm font-bold tabular-nums">
                      {p.homeScore} × {p.awayScore}
                    </span>
                    {p.pointsEarned !== null && (
                      <Badge variant={p.isOfficial ? "success" : "default"}>
                        +{p.pointsEarned}
                        {!p.isOfficial && " (provisório)"}
                      </Badge>
                    )}
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
